import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import carImg from '../../assets/img/vehicles/car.png';
import scooterImg from '../../assets/img/vehicles/scooter.png';
import kickImg from '../../assets/img/vehicles/kickscooter.png';
import bikeImg from '../../assets/img/vehicles/bike.png';
import vanImg from '../../assets/img/vehicles/van.png';
import motoImg from '../../assets/img/vehicles/motorcycle.png';
import quadImg from '../../assets/img/vehicles/quad.png';
import jetskiImg from '../../assets/img/vehicles/jetski.png';
import boatImg from '../../assets/img/vehicles/boat.png';

/*
 * Briques du back-office, dessinées comme l'app (docs/backoffice/maquette-bo-*.png) :
 * Figtree, indigo #4F46E5, titres #1E293B, gris #6B7280, bandes #F1F2F8,
 * libellés de section en petites capitales 11, lignes 14 / 12, pastilles 10,5.
 */

export const DG = {
  indigo: '#4F46E5',
  title: '#1E293B',
  grey: '#6B7280',
  band: '#F1F2F8',
  line: '#EEF0F4',
  softIndigo: '#EEEDFD',
};

/* ------------------------------------------------------------------ */
/* En-tête : ce qu'une page pose à côté de son titre (onglets, filtres). */
/* ------------------------------------------------------------------ */

export const HEADER_ACTIONS_ID = 'dg-header-actions';

/** Rend ses enfants dans l'en-tête, à droite du titre. */
export const HeaderActions: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  useEffect(() => { setTarget(document.getElementById(HEADER_ACTIONS_ID)); }, []);
  return target ? createPortal(children, target) : null;
};

/* ------------------------------------------------------------------ */
/* Icônes au trait                                                     */
/* ------------------------------------------------------------------ */

export const LineIcon: React.FC<{ d: string; size?: number; color?: string; width?: number; className?: string }> = ({
  d, size = 20, color = 'currentColor', width = 1.8, className,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={width}
    strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d={d} />
  </svg>
);

export const ICONS = {
  chevron: 'm9 6 6 6-6 6',
  close: 'M6 6l12 12M18 6 6 18',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  search: 'M11 4.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM16 16l4 4',
  filter: 'M4 6h16l-6 7v5l-4 2v-7z',
  phone: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z',
  mail: 'M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM4 7l8 6 8-6',
  message: 'M4 5h16v11H9l-5 4z',
  journal: 'M6 3h12v18H6zM9 7h6M9 11h6M9 15h4',
  lock: 'M7 11h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2zM8 11V8a4 4 0 0 1 8 0v3',
  alert: 'M12 3l9 16H3zM12 10v4M12 17v.5',
  flag: 'M5 21V4h11l-2 4 2 4H5',
  doc: 'M6 3h9l4 4v14H6zM14 3v5h5M9 13h6M9 17h4',
  money: 'M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2zM12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z',
  car: 'M5 16V11l2-5h10l2 5v5zM5 16v2M19 16v2',
  help: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM5.6 5.6l3.6 3.6M14.8 14.8l3.6 3.6M18.4 5.6l-3.6 3.6M9.2 14.8l-3.6 3.6',
  megaphone: 'M3 11v2a1 1 0 0 0 1 1h3l6 4V6L7 10H4a1 1 0 0 0-1 1zM17 9a4 4 0 0 1 0 6',
  dots: 'M12 5h.01M12 12h.01M12 19h.01',
  user: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4 21c1-4 4-6 8-6s7 2 8 6',
};

/* ------------------------------------------------------------------ */
/* Avatar à initiales (ou photo)                                       */
/* ------------------------------------------------------------------ */

export type AvatarTone = 'indigo' | 'grey' | 'orange' | 'green';
const AVATAR_TONES: Record<AvatarTone, string> = {
  indigo: 'bg-[#EEEDFD] text-[#4F46E5]',
  grey: 'bg-[#F1F2F8] text-[#475569]',
  orange: 'bg-[#FFF4DB] text-[#B45309]',
  green: 'bg-[#E7F7EF] text-[#047857]',
};

export const initialsOf = (name?: string | null) =>
  (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

export const Avatar: React.FC<{ name?: string | null; photo?: string | null; size?: number; tone?: AvatarTone; ring?: boolean }> = ({
  name, photo, size = 36, tone = 'indigo', ring,
}) => {
  const [broken, setBroken] = useState(false);
  const inner = photo && !broken ? (
    <img src={photo} alt="" onError={() => setBroken(true)} className="h-full w-full rounded-full object-cover" />
  ) : (
    <span className={`flex h-full w-full items-center justify-center rounded-full font-extrabold ${AVATAR_TONES[tone]}`}
      style={{ fontSize: Math.round(size / 2.8) }}>
      {initialsOf(name)}
    </span>
  );
  if (!ring) return <span className="inline-flex shrink-0" style={{ width: size, height: size }}>{inner}</span>;
  return (
    <span className="inline-flex shrink-0 rounded-full border-2 border-[#4F46E5] p-[3px]" style={{ width: size + 10, height: size + 10 }}>
      {inner}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Vignette d'un véhicule : sa vraie photo, à défaut le rendu de l'app  */
/* ------------------------------------------------------------------ */

const TYPE_IMAGES: Record<string, string> = {
  car: carImg, voiture: carImg, scooter: scooterImg, kickscooter: kickImg, trottinette: kickImg,
  bike: bikeImg, velo: bikeImg, vélo: bikeImg, van: vanImg, utility: vanImg, motorcycle: motoImg, moto: motoImg,
  quad: quadImg, jetski: jetskiImg, boat: boatImg, bateau: boatImg,
};

export const vehiclePhoto = (vehicle: any): string | null => {
  if (!vehicle) return null;
  const images = Array.isArray(vehicle.images) ? [...vehicle.images] : [];
  images.sort((a: any, b: any) => (a?.position ?? 0) - (b?.position ?? 0));
  const first = images[0];
  const url = first?.thumb_url || first?.url || vehicle.thumb_url || vehicle.cover_url || vehicle.image_url || vehicle.vehicle_photo || null;
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return `${process.env.REACT_APP_API_URL || ''}${String(url).replace(/^\//, '')}`;
};

export const vehicleTypeImage = (type?: string | null) => TYPE_IMAGES[(type || '').toLowerCase()] || carImg;

export const VehicleThumb: React.FC<{ vehicle?: any; type?: string | null; size?: number; radius?: number; dimmed?: boolean }> = ({
  vehicle, type, size = 44, radius = 12, dimmed,
}) => {
  const [broken, setBroken] = useState(false);
  const src = vehiclePhoto(vehicle);
  const style: React.CSSProperties = { width: size, height: size, borderRadius: radius };
  if (src && !broken) {
    return (
      <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} style={style}
        className={`shrink-0 bg-[#F1F2F8] object-cover ${dimmed ? 'grayscale opacity-70' : ''}`} />
    );
  }
  return (
    <span style={style} className="flex shrink-0 items-center justify-center bg-[#F1F2F8]">
      <img src={vehicleTypeImage(type || vehicle?.vehicle_type)} alt="" style={{ width: '78%' }} className={dimmed ? 'grayscale opacity-70' : ''} />
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Pastilles d'état, de filtre, onglets                                 */
/* ------------------------------------------------------------------ */

export type Tone = 'green' | 'indigo' | 'amber' | 'grey' | 'red';
const TONES: Record<Tone, string> = {
  green: 'bg-[#E7F7EF] text-[#047857]',
  indigo: 'bg-[#EEEDFD] text-[#4F46E5]',
  amber: 'bg-[#FEF3C7] text-[#B45309]',
  grey: 'bg-[#F1F2F8] text-[#475569]',
  red: 'bg-[#FDECEF] text-[#B91C1C]',
};

export const StatusPill: React.FC<{ tone: Tone; children: React.ReactNode; className?: string }> = ({ tone, children, className = '' }) => (
  <span className={`inline-flex items-center whitespace-nowrap rounded-lg px-2 py-[3px] text-[10.5px] font-bold leading-[14px] ${TONES[tone]} ${className}`}>
    {children}
  </span>
);

/** Une pastille de filtre (32 de haut), sombre quand elle est choisie. */
export const FilterChip: React.FC<{
  active?: boolean; count?: number | string | null; countTone?: 'amber' | 'red'; onClick?: () => void; children: React.ReactNode; icon?: React.ReactNode;
}> = ({ active, count, countTone, onClick, children, icon }) => (
  <button type="button" onClick={onClick}
    className={`inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-[13px] text-[12.5px] font-bold transition-colors ${
      active ? 'border-[#1E293B] bg-[#1E293B] text-white' : 'border-[#E5E7EB] bg-white text-[#4B5563] hover:bg-gray-50'
    }`}>
    {icon}
    {children}
    {count !== undefined && count !== null && count !== '' && (
      <span className={`flex h-[18px] min-w-[20px] items-center justify-center rounded-full px-[5px] text-[10.5px] ${
        active ? 'bg-white/15 text-white'
          : countTone === 'amber' ? 'bg-[#FEF3C7] text-[#B45309]'
          : countTone === 'red' ? 'bg-[#FDECEF] text-[#B91C1C]'
          : 'bg-[#F1F2F8] text-[#4B5563]'
      }`}>{count}</span>
    )}
  </button>
);

/** Onglets segmentés (fond gris, l'actif en blanc), comme Résumé / Suivi dans l'app. */
export const Segmented: React.FC<{ options: { id: string; label: string; badge?: string }[]; value: string; onChange: (id: string) => void; ariaLabel?: string }> = ({
  options, value, onChange, ariaLabel,
}) => (
  <div role="tablist" aria-label={ariaLabel} className="inline-flex gap-1 rounded-[19px] bg-[#EEEFF5] p-[3px]">
    {options.map((o) => (
      <button key={o.id} type="button" role="tab" aria-selected={o.id === value} onClick={() => onChange(o.id)}
        className={`h-8 whitespace-nowrap rounded-2xl px-4 text-[12.5px] transition-colors ${
          o.id === value ? 'bg-white font-extrabold text-[#1E293B] shadow-[0_1px_4px_rgba(0,0,0,0.13)]' : 'font-semibold text-[#6B7280] hover:text-[#1E293B]'
        }`}>
        {o.label}
        {o.badge && <span className="ml-1.5 rounded-full bg-[#FEF3C7] px-1.5 py-[1px] align-[1px] text-[10px] font-bold text-[#92400E]">{o.badge}</span>}
      </button>
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/* Cartes, libellés, lignes                                             */
/* ------------------------------------------------------------------ */

export const Card: React.FC<{ className?: string; children: React.ReactNode; style?: React.CSSProperties }> = ({ className = '', children, style }) => (
  <div style={style} className={`rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}>{children}</div>
);

export const SectionLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <span className={`text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280] ${className}`}>{children}</span>
);

/** Titre de carte (petites capitales) et action indigo à droite. */
export const CardHeader: React.FC<{ title: React.ReactNode; action?: React.ReactNode; className?: string }> = ({ title, action, className = '' }) => (
  <div className={`flex items-center justify-between px-5 pb-1.5 pt-4 ${className}`}>
    <SectionLabel>{title}</SectionLabel>
    {action}
  </div>
);

export const LinkButton: React.FC<{ onClick?: () => void; children: React.ReactNode; disabled?: boolean }> = ({ onClick, children, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className="text-[13px] font-bold text-[#4F46E5] hover:underline disabled:opacity-40">{children}</button>
);

export const Band: React.FC<{ className?: string }> = ({ className = '' }) => <div className={`h-2 bg-[#F1F2F8] ${className}`} />;

/** Une action en ligne : icône ronde, titre 14, sous-titre 12, chevron. */
export const ActionRow: React.FC<{
  icon: string; title: string; subtitle?: string; tone?: 'indigo' | 'grey' | 'red' | 'amber'; onClick?: () => void; first?: boolean; disabled?: boolean; inset?: number;
}> = ({ icon, title, subtitle, tone = 'indigo', onClick, first, disabled, inset = 22 }) => {
  const colors = { indigo: ['#EEEDFD', '#4F46E5'], grey: ['#F1F2F8', '#475569'], red: ['#FDECEF', '#B91C1C'], amber: ['#FFF4DB', '#B45309'] }[tone];
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="flex w-full items-center gap-3 py-[11px] text-left disabled:opacity-50"
      style={{ marginLeft: inset, marginRight: inset, width: `calc(100% - ${inset * 2}px)`, borderTop: first ? 'none' : `1px solid ${DG.line}` }}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style={{ background: colors[0] }}>
        <LineIcon d={(ICONS as any)[icon] || icon} size={17} color={colors[1]} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-[14px] font-semibold ${tone === 'red' ? 'text-[#B91C1C]' : 'text-[#1E293B]'}`}>{title}</span>
        {subtitle && <span className="mt-px block truncate text-[12px] text-[#6B7280]">{subtitle}</span>}
      </span>
      <LineIcon d={ICONS.chevron} size={16} color="#9CA3AF" width={2.4} />
    </button>
  );
};

/** Chiffre clé : libellé, grand nombre, une ligne dessous. */
export const KpiCard: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; color?: string; valueColor?: string; subColor?: string; highlight?: boolean; onClick?: () => void }> = ({
  label, value, sub, color, valueColor, subColor, highlight, onClick,
}) => (
  <button type="button" onClick={onClick} disabled={!onClick}
    className={`rounded-[20px] border px-5 py-[18px] text-left transition-colors ${
      highlight ? 'border-[#FDE9C3] bg-[#FFF8EB] hover:bg-[#FFF3DC]' : 'border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] enabled:hover:bg-gray-50'
    }`}>
    <span className={`block text-[13px] font-semibold ${highlight ? 'text-[#B45309]' : 'text-[#6B7280]'}`}>{label}</span>
    <span className="mt-1.5 block text-[30px] font-extrabold leading-none tracking-[-0.5px]" style={{ color: valueColor || (highlight ? '#B45309' : (color && color !== '#1E293B' ? color : '#1E293B')) }}>{value}</span>
    {sub && <span className="mt-2 block text-[12px] font-semibold" style={{ color: subColor || color || '#6B7280' }}>{sub}</span>}
  </button>
);

/** Bouton 48 : primaire indigo, secondaire gris, danger rouge doux. */
export const BigButton: React.FC<{ kind?: 'primary' | 'secondary' | 'danger'; onClick?: () => void; disabled?: boolean; children: React.ReactNode; className?: string; type?: 'button' | 'submit' }> = ({
  kind = 'primary', onClick, disabled, children, className = '', type = 'button',
}) => (
  <button type={type} onClick={onClick} disabled={disabled}
    className={`inline-flex h-12 items-center justify-center gap-2 rounded-3xl px-6 text-[15px] font-bold transition-colors disabled:opacity-50 ${
      kind === 'primary' ? 'bg-[#4F46E5] text-white hover:bg-[#4338CA]'
        : kind === 'danger' ? 'bg-[#FDECEF] text-[#B91C1C] hover:bg-[#FBDDE2]'
        : 'bg-[#F1F2F8] text-[#1E293B] hover:bg-[#E7E9F2]'
    } ${className}`}>
    {children}
  </button>
);

/** Ferme un volet latéral (rond blanc à bord gris). */
export const CloseButton: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label = 'Fermer' }) => (
  <button type="button" onClick={onClick} aria-label={label}
    className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E5E7EB] bg-white hover:bg-gray-50">
    <LineIcon d={ICONS.close} size={14} color="#1E293B" width={2.4} />
  </button>
);

export const formatDT = (n?: number | string | null) => {
  const v = Number(n || 0);
  return `${v.toLocaleString('fr-FR', { maximumFractionDigits: v % 1 === 0 ? 0 : 1 })} DT`;
};

/** Champ de 52 et zone de texte, comme dans l'app. */
export const FIELD = 'h-[52px] w-full rounded-2xl border border-[#E5E7EB] bg-white px-4 text-[15px] text-[#1E293B] placeholder-[#9CA3AF] focus:border-[#C7C4F6] focus:outline-none focus:ring-4 focus:ring-[#4F46E5]/10';
export const AREA = 'w-full resize-none rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3.5 text-[15px] leading-[1.55] text-[#1E293B] placeholder-[#9CA3AF] focus:border-[#C7C4F6] focus:outline-none focus:ring-4 focus:ring-[#4F46E5]/10';

/** Libellé de champ en petites capitales. */
export const FieldLabel: React.FC<{ children: React.ReactNode; htmlFor?: string; className?: string }> = ({ children, htmlFor, className = '' }) => (
  <label htmlFor={htmlFor} className={`mb-2 mt-[18px] block text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280] ${className}`}>{children}</label>
);

/** Interrupteur 44 × 26. */
export const Switch: React.FC<{ checked: boolean; onChange: (value: boolean) => void; label: string; disabled?: boolean }> = ({ checked, onChange, label, disabled }) => (
  <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)}
    className={`relative h-[26px] w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${checked ? 'bg-[#4F46E5]' : 'bg-[#E5E7EB]'}`}>
    <span className={`absolute top-[3px] h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-all ${checked ? 'left-[21px]' : 'left-[3px]'}`} />
  </button>
);

/** Fenêtre par-dessus l'écran : titre, sous-titre, fermeture ronde ; Échap et le voile la ferment. */
export const Modal: React.FC<{ title: React.ReactNode; subtitle?: React.ReactNode; onClose: () => void; width?: number; children: React.ReactNode }> = ({
  title, subtitle, onClose, width = 520, children,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#0F172A]/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" className="max-h-[92vh] w-full overflow-y-auto rounded-3xl bg-white px-7 pb-7 pt-6 shadow-[0_24px_60px_rgba(15,23,42,0.28)]" style={{ maxWidth: width }}>
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-[20px] font-extrabold leading-[26px] text-[#1E293B]">{title}</h2>
            {subtitle && <p className="mt-1 text-[13.5px] leading-[1.45] text-[#6B7280]">{subtitle}</p>}
          </div>
          <CloseButton onClick={onClose} />
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};
